<template>
  <div class="pedmat-page">
    <!-- Header -->
    <div class="pm-header">
      <div>
        <span class="header-eyebrow">Inventario</span>
        <h2 class="header-title">Pedidos de Materiales</h2>
      </div>
      <div class="header-actions">
        <button class="btn-pm btn-pm--primary" @click="nuevoPedido()">
          <i class="fa-solid fa-plus"></i> Nuevo pedido
        </button>
      </div>
    </div>

    <!-- KPI Strip -->
    <div class="kpi-strip">
      <div class="kpi-card">
        <span class="kpi-label">Total</span>
        <span class="kpi-value">{{ listadoPedidos.length }}</span>
        <span class="kpi-foot">pedidos</span>
      </div>
      <div class="kpi-card">
        <span class="kpi-label">Sin pedir</span>
        <span class="kpi-value kpi-value--warning">{{ countSinPedir }}</span>
        <span class="kpi-foot">pendientes</span>
      </div>
      <div class="kpi-card">
        <span class="kpi-label">Sin recibir</span>
        <span class="kpi-value kpi-value--danger">{{ countSinRecibir }}</span>
        <span class="kpi-foot">en transito</span>
      </div>
      <div class="kpi-card">
        <span class="kpi-label">Mostrando</span>
        <span class="kpi-value">{{ pedidosBusqueda.length }}</span>
        <span class="kpi-foot">resultados</span>
      </div>
    </div>

    <!-- Filters -->
    <div class="filters-bar">
      <div class="search-box">
        <i class="fa-solid fa-magnifying-glass search-box__icon"></i>
        <input type="text" class="search-box__input" placeholder="Buscar pedido, proveedor, material..." v-model="buscar" />
      </div>

      <div class="filter-group">
        <label class="filter-label">Proveedor</label>
        <v-select v-model="proveedorSelect" :options="proveedores" label="nombre" :clearable="false" class="select-styled select-filter" />
      </div>

      <div class="filter-group">
        <label class="filter-label">Desde</label>
        <flat-pickr v-model="fechaDesdeSelect" class="filter-date" placeholder="Fecha" />
      </div>

      <div class="filter-group">
        <label class="filter-label">Hasta</label>
        <flat-pickr v-model="fechaHastaSelect" class="filter-date" placeholder="Fecha" />
      </div>

      <div class="filter-pills">
        <div class="pill-group">
          <label class="filter-label">Pedido</label>
          <div class="pills-row">
            <button :class="['pill', { active: estadoPedidoSelect === 'Todos' }]" @click="estadoPedidoSelect = 'Todos'">Todos</button>
            <button :class="['pill', { active: estadoPedidoSelect === 'Sin Pedir' }]" @click="estadoPedidoSelect = 'Sin Pedir'">Sin pedir</button>
            <button :class="['pill', { active: estadoPedidoSelect === 'Pedido' }]" @click="estadoPedidoSelect = 'Pedido'">Pedido</button>
          </div>
        </div>
        <div class="pill-group">
          <label class="filter-label">Recibido</label>
          <div class="pills-row">
            <button :class="['pill', { active: estadoSelect === 'Todos' }]" @click="estadoSelect = 'Todos'">Todos</button>
            <button :class="['pill', { active: estadoSelect === 'Sin Recibir' }]" @click="estadoSelect = 'Sin Recibir'">Sin recibir</button>
            <button :class="['pill', { active: estadoSelect === 'Recibido' }]" @click="estadoSelect = 'Recibido'">Recibido</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Table -->
    <div class="list-card">
      <div class="table-wrapper">
        <b-table
          :items="pedidosBusqueda"
          :fields="columnasTable"
          class="mb-0"
          hover
          sort-icon-left
          responsive
          show-empty
          empty-text="No se encontraron pedidos"
          @sort-changed="sortChanged"
          thead-class="table-head"
          tbody-tr-class="table-row"
        >
          <template #cell(codigo)="data">
            <span class="cell-code">{{ data.item.codigo }}</span>
          </template>

          <template #cell(totalMateriales)="data">
            <span class="cell-count-badge">{{ data.item.totalMateriales }}</span>
          </template>

          <template #cell(proveedor)="data">
            <span class="cell-text">{{ data.item.proveedor }}</span>
          </template>

          <template #cell(fecha)="data">
            <span class="cell-mono">{{ data.item.fecha }}</span>
          </template>

          <template #cell(pedido)="data">
            <span v-if="data.item.fechaPedido.length == 0" class="status-badge status-badge--pending" @click="clickPedido(true, data.item.id)">
              <i class="fa-solid fa-clock"></i> Pendiente
            </span>
            <span v-else class="status-badge status-badge--done" @click="clickPedido(false, data.item.id)">
              <i class="fa-solid fa-check"></i> {{ data.item.fechaPedido }}
              <button class="status-badge__edit" @click.stop="editarFechaPedido(data.item)"><i class="fa-solid fa-pen"></i></button>
            </span>
          </template>

          <template #cell(recibido)="data">
            <span v-if="data.item.fechaRecibido.length == 0" class="status-badge status-badge--pending" @click="clickRecibido(true, data.item.id)">
              <i class="fa-solid fa-clock"></i> Pendiente
            </span>
            <span v-else class="status-badge status-badge--done" @click="clickRecibido(false, data.item.id)">
              <i class="fa-solid fa-check"></i> {{ data.item.fechaRecibido }}
              <button class="status-badge__edit" @click.stop="editarFechaRecibido(data.item)"><i class="fa-solid fa-pen"></i></button>
            </span>
          </template>

          <template #cell(acciones)="data">
            <div class="cell-actions">
              <button class="action-btn action-btn--email" @click="enviarEmail(data.item)" title="Enviar email">
                <i class="fa-regular fa-envelope"></i>
              </button>
              <button class="action-btn action-btn--view" @click="data.toggleDetails" title="Ver materiales">
                <i :class="data.detailsShowing ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
              </button>
              <button class="action-btn action-btn--delete" @click="borrarPedido(data.item.id)" title="Eliminar">
                <i class="fa-solid fa-trash"></i>
              </button>
            </div>
          </template>

          <template #row-details="row">
            <div class="details-wrap">
              <div class="details-inner">
                <b-table
                  :items="row.item.listaMateriales"
                  :fields="columnasMateriales"
                  class="mb-0"
                  small
                  thead-class="details-head"
                  tbody-tr-class="details-row"
                />
              </div>
            </div>
          </template>
        </b-table>
      </div>

      <div class="list-card__footer">
        <span class="footer-count">{{ pedidosBusqueda.length }} pedidos</span>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@axios'
import { BTable , BFormCheckbox, BFormInput, BFormGroup, BModal, BCardText, BCardBody, BCard, BRow, BCol} from 'bootstrap-vue'
import BCardCode from '@core/components/b-card-code'
import { $themeColors } from '@themeConfig'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'

  export default {
    components: {
        BCardCode, BFormCheckbox, BTable, BFormInput, BFormGroup,
        BCardText, BCardBody, BCard, BRow, BCol, vSelect, flatPickr,
    },
    data () {
      return {
        columnasTable: [
          { key: "codigo", label: "Codigo", sortable: true },
          { key: "totalMateriales", label: "Mat.", sortable: true, thStyle: { width: "70px" } },
          { key: "proveedor", label: "Proveedor", sortable: true },
          { key: "fecha", label: "Fecha", sortable: true },
          { key: "pedido", label: "Pedido", sortable: true },
          { key: "recibido", label: "Recibido", sortable: true },
          { key: "acciones", label: "", thStyle: { width: "110px" } },
        ],
        columnasMateriales: [
          { key: "nombre", label: "Nombre", sortable: false },
          { key: "color", label: "Color", sortable: false },
          { key: "cantidad", label: "Pedido", sortable: false },
          { key: "cantidadRecibida", label: "Recibido", sortable: false },
          { key: "cantidadFacturada", label: "Facturado", sortable: false },
        ],
        buscar: "",
        lastSort: null,
        pedidosBusqueda: [],
        listadoPedidos: [],
        fechaDesdeSelect: '',
        fechaHastaSelect: '',
        estadoSelect: 'Todos',
        estadoPedidoSelect: 'Todos',
        proveedorSelect: '',
        listadoProveedores: [],
        proveedores: [],
        itemSelect: null,
      }
    },
    computed: {
      countSinPedir() {
        return this.listadoPedidos.filter(p => p.fechaPedido === '').length
      },
      countSinRecibir() {
        return this.listadoPedidos.filter(p => p.fechaRecibido === '').length
      }
    },
    methods:{
      loadItems(){
        axios.post("/uso", {seccion:"listadoPedidos"});

        axios.get("/materiales/pedidos").then(r => r.data).then(res => {
          this.listadoPedidos = res.sort((a, b) => b.id - a.id);
          this.pedidosBusqueda = this.listadoPedidos;
          this.buscarFuncion();
        });

        axios.get("/proveedores").then(r => r.data).then(res => {
          this.listadoProveedores = res;
          this.listadoProveedores.sort((a, b) => a.nombre.localeCompare(b.nombre));
          this.proveedores = JSON.parse(JSON.stringify(res));
          this.proveedores.sort((a, b) => a.nombre.localeCompare(b.nombre));
          this.proveedores.unshift({id: 0, nombre: 'Todos'})
          this.proveedorSelect = this.proveedores[0];
        });
      },
      sortChanged(e) {
        this.lastSort = e;
        this.buscarFuncion();
      },
      enviarEmail(data){
        let textoEmail = `mailto:${data.emailProveedor}?subject=Pedido nuevo - Harber London / Craft Lab&body=Buenas tardes,%0D%0A%0D%0ANecesito un pedido nuevo por favor:%0D%0A`;

        data.listaMateriales.forEach(element => {
          let nombre = '';
          if(element.ref.length > 2)
            nombre = element.ref
          else if(element.color.length > 1)
            nombre = `${element.nombre} ${element.color}`
          else
            nombre = element.nombre;
          textoEmail += `%0D%0A${nombre}: ${element.cantidad}`;
        });

        textoEmail += `%0D%0A%0D%0AGracias,%0D%0AVero`;
        window.location.href = textoEmail;
      },
      clickPedido(estado, id){
        axios.post("/materiales/pedidos/fechaPedido", {estado, id}).then(r => r.data).then(res => {
          this.pedidosBusqueda.forEach(item => { if(item.id == id) item.fechaPedido = res; });
          this.listadoPedidos.forEach(item => { if(item.id == id) item.fechaPedido = res; });
        });
      },
      clickRecibido(estado, id){
        axios.post("/materiales/pedidos/fechaRecibido", {estado, id}).then(r => r.data).then(res => {
          this.pedidosBusqueda.forEach(item => { if(item.id == id) item.fechaRecibido = res; });
          this.listadoPedidos.forEach(item => { if(item.id == id) item.fechaRecibido = res; });
        });
      },
      editarFechaRecibido(data){
        this.$swal({
          title: 'Seleccionar nueva fecha. YYYY-MM-DD',
          icon: 'info',
          input: "text",
          showCancelButton: true,
          confirmButtonText: 'Guardar',
          cancelButtonText: 'Cancelar',
          customClass: { confirmButton: 'btn btn-primary', cancelButton: 'btn btn-outline-danger ml-1' },
          buttonsStyling: false,
        }).then(result => {
          if(result.isConfirmed) {
            axios.put("/materiales/pedidos/fechaRecibido", {fechaRecibido: result.value, id: data.id}).then(r => r.data).then(res => {
              this.pedidosBusqueda.forEach(item => { if(item.id == data.id) item.fechaRecibido = result.value; });
              this.listadoPedidos.forEach(item => { if(item.id == data.id) item.fechaRecibido = res; });
            });
          }
        })
      },
      editarFechaPedido(data){
        this.$swal({
          title: 'Seleccionar nueva fecha. YYYY-MM-DD',
          icon: 'info',
          input: "text",
          showCancelButton: true,
          confirmButtonText: 'Guardar',
          cancelButtonText: 'Cancelar',
          customClass: { confirmButton: 'btn btn-primary', cancelButton: 'btn btn-outline-danger ml-1' },
          buttonsStyling: false,
        }).then(result => {
          if(result.isConfirmed) {
            axios.put("/materiales/pedidos/fechaPedido", {fechaPedido: result.value, id: data.id}).then(r => r.data).then(res => {
              this.pedidosBusqueda.forEach(item => { if(item.id == data.id) item.fechaPedido = result.value; });
              this.listadoPedidos.forEach(item => { if(item.id == data.id) item.fechaPedido = res; });
            });
          }
        })
      },
      buscarFuncion(items = this.listadoPedidos){
        let auxSorting = JSON.parse(JSON.stringify(items));

        if(this.lastSort != null) {
          if(this.lastSort.sortDesc)
            auxSorting.sort((a,b) => b[this.lastSort.sortBy] - a[this.lastSort.sortBy]);
          else
            auxSorting.sort((a,b) => a[this.lastSort.sortBy] - b[this.lastSort.sortBy]);
        }

        items = auxSorting;
        this.pedidosBusqueda = [];

        this.pedidosBusqueda = items.filter(factura => {
          const recibidoCondicion =
            this.estadoSelect === "Todos" ||
            (this.estadoSelect === "Sin Recibir" && factura.fechaRecibido === '') ||
            (this.estadoSelect === "Recibido" && factura.fechaRecibido !== '');

          const pedidoCondicion =
            this.estadoPedidoSelect === "Todos" ||
            (this.estadoPedidoSelect === "Sin Pedir" && factura.fechaPedido === '') ||
            (this.estadoPedidoSelect === "Pedido" && factura.fechaPedido !== '');

          const proveedorCondicion = this.proveedorSelect.id === 0 || this.proveedorSelect.id === factura.idProveedor;
          const textoCondicion = factura.codigo.toLowerCase().includes(this.buscar.toLowerCase()) || factura.proveedor.toLowerCase().includes(this.buscar.toLowerCase()) || factura.lista.toLowerCase().includes(this.buscar.toLowerCase());
          const fechaDesdeCondicion = !this.fechaDesdeSelect || new Date(factura.fechaFactura) >= new Date(this.fechaDesdeSelect);
          const fechaHastaCondicion = !this.fechaHastaSelect || new Date(factura.fechaFactura) <= new Date(this.fechaHastaSelect);

          return recibidoCondicion && pedidoCondicion && textoCondicion && proveedorCondicion && fechaDesdeCondicion && fechaHastaCondicion;
        });
      },
      nuevoPedido(){
        this.$router.push('/nuevo-pedido-materiales');
      },
      borrarPedido(data){
        this.$swal({
          title: 'Eliminar pedido',
          text: 'Esta accion no se puede deshacer',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Eliminar',
          cancelButtonText: 'Cancelar',
          customClass: { confirmButton: 'btn btn-outline-danger', cancelButton: 'btn btn-primary ml-1' },
          buttonsStyling: false,
        }).then(result => {
          if(result.isConfirmed) {
            axios.post("/materiales/pedidos/delete", {id: data}).then(r => r.data).then(res => {
              this.listadoPedidos = this.listadoPedidos.filter(item => item.id !== data);
              this.buscarFuncion();
            });
          }
        })
      }
    },
    mounted() { this.loadItems(); },
    watch: {
      buscar(){ this.buscarFuncion(); },
      estadoSelect(){ this.buscarFuncion(); },
      estadoPedidoSelect(){ this.buscarFuncion(); },
      proveedorSelect(){ this.buscarFuncion(); },
      fechaDesdeSelect(){ this.buscarFuncion(); },
      fechaHastaSelect(){ this.buscarFuncion(); },
    }
  }
</script>

<style lang="scss" scoped>
$primary: #3B82F6;
$primary-dark: #2563EB;
$success: #10B981;
$danger: #EF4444;
$warning: #F59E0B;
$info: #06B6D4;
$border: #E2E8F0;
$text: #1E293B;
$text-muted: #64748B;
$bg: #F8FAFC;
$card-bg: #FFFFFF;
$radius: 12px;
$radius-sm: 8px;
$shadow: 0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04);

.pedmat-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 8px;
}

// Header
.pm-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 20px;
}

.header-eyebrow {
  display: block;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: $primary;
  margin-bottom: 2px;
}

.header-title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: $text;
  margin: 0;
}

.btn-pm {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 20px;
  border-radius: $radius-sm;
  border: none;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &--primary { background: $primary; color: white; &:hover { background: $primary-dark; } }
}

// KPI Strip
.kpi-strip {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.kpi-card {
  background: $card-bg;
  border: 1px solid $border;
  border-radius: $radius-sm;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  box-shadow: $shadow;
}

.kpi-label {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: $text-muted;
}

.kpi-value {
  font-family: 'JetBrains Mono', monospace;
  font-size: 22px;
  font-weight: 700;
  color: $text;
  font-feature-settings: "tnum";
  margin: 4px 0 2px;

  &--warning { color: $warning; }
  &--danger { color: $danger; }
}

.kpi-foot {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  color: $text-muted;
}

// Filters
.filters-bar {
  display: flex;
  align-items: flex-end;
  gap: 14px;
  padding: 16px 20px;
  background: $card-bg;
  border: 1px solid $border;
  border-radius: $radius;
  box-shadow: $shadow;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.search-box {
  position: relative;
  flex: 1;
  min-width: 200px;
  max-width: 280px;

  &__icon {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: $text-muted;
    font-size: 12px;
  }

  &__input {
    width: 100%;
    padding: 7px 12px 7px 32px;
    border: 1px solid $border;
    border-radius: $radius-sm;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 13px;
    color: $text;
    background: transparent;

    &::placeholder { color: #CBD5E1; }
    &:focus { outline: none; border-color: $primary; }
  }
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.filter-label {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: $text-muted;
  margin: 0;
}

.filter-pills {
  display: flex;
  gap: 14px;
  margin-left: auto;
}

.pill-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.pills-row {
  display: flex;
  gap: 4px;
}

.pill {
  padding: 5px 11px;
  border-radius: 20px;
  border: 1px solid $border;
  background: transparent;
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 500;
  color: $text-muted;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover { border-color: $primary; color: $primary; }
  &.active { background: $primary; border-color: $primary; color: white; }
}

// List card
.list-card {
  background: $card-bg;
  border: 1px solid $border;
  border-radius: $radius;
  box-shadow: $shadow;
  overflow: hidden;

  &__footer {
    padding: 12px 22px;
    border-top: 1px solid $border;
  }
}

.table-wrapper { padding: 0; }

// Los ::v-deep van bajo .pedmat-page a proposito: en la raiz, postcss-rtl + scoped generan [dir][data-v-xxx]
// y ninguna propiedad direccional (padding, margin...) llega a aplicarse.
.pedmat-page ::v-deep .table-head th {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: $text-muted;
  border-top: none;
  border-bottom: 1px solid $border;
  padding: 10px 14px;
  background: transparent;
}

.pedmat-page ::v-deep .table-row td {
  padding: 10px 14px;
  vertical-align: middle;
  border-color: lighten($border, 3%);
  font-size: 13px;
}

.pedmat-page ::v-deep .table-row:hover td { background: $bg; }

.cell-code {
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  font-weight: 600;
  color: $primary;
}

.cell-count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 26px;
  height: 22px;
  padding: 0 7px;
  border-radius: 12px;
  background: rgba($primary, 0.08);
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  font-weight: 700;
  color: $primary;
}

.cell-text { font-size: 13px; color: $text; }
.cell-mono { font-family: 'JetBrains Mono', monospace; font-size: 12px; color: $text; font-feature-settings: "tnum"; }

// Status badges
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 20px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &--pending {
    background: rgba($warning, 0.1);
    color: darken($warning, 15%);

    &:hover { background: rgba($warning, 0.18); }
  }

  &--done {
    background: rgba($success, 0.1);
    color: darken($success, 10%);

    &:hover { background: rgba($success, 0.18); }
  }

  &__edit {
    border: none;
    background: transparent;
    padding: 0 2px;
    margin-left: 2px;
    cursor: pointer;
    color: inherit;
    opacity: 0.5;
    font-size: 9px;
    transition: opacity 0.15s;

    &:hover { opacity: 1; }
  }
}

// Actions
.cell-actions {
  display: flex;
  gap: 4px;
}

.action-btn {
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 1px solid transparent;
  background: transparent;
  color: $text-muted;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  font-size: 12px;

  &--email:hover { background: rgba($success, 0.1); color: $success; border-color: rgba($success, 0.2); }
  &--view:hover { background: rgba($primary, 0.1); color: $primary; border-color: rgba($primary, 0.2); }
  &--delete:hover { background: rgba($danger, 0.1); color: $danger; border-color: rgba($danger, 0.2); }
}

// Details
.details-wrap {
  display: flex;
  justify-content: center;
  padding: 4px 0 10px;
}

.details-inner {
  width: 75%;
  background: $bg;
  border: 1px solid $border;
  border-radius: $radius-sm;
  padding: 8px 12px;
  overflow: hidden;
}

.pedmat-page ::v-deep .details-head th {
  font-family: 'JetBrains Mono', monospace;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: $text-muted;
  border-bottom: 1px solid $border;
  border-top: none;
  padding: 6px 10px;
}

.pedmat-page ::v-deep .details-row td {
  border: none;
  padding: 5px 10px;
  font-size: 12px;
  color: $text;
}

.footer-count {
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  color: $text-muted;
}

// Responsive
@media (max-width: 768px) {
  .kpi-strip { grid-template-columns: repeat(2, 1fr); }
  .pm-header { flex-direction: column; align-items: flex-start; gap: 12px; }
  .filters-bar { flex-direction: column; align-items: stretch; }
  .search-box { max-width: none; }
  .filter-pills { margin-left: 0; flex-wrap: wrap; }
  .details-inner { width: 100%; }
}
</style>

<style lang="scss">
@import '../@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';

.pedmat-page {
  .select-styled {
    .vs__dropdown-toggle {
      min-height: 33px;
      border-radius: 8px;
      border: 1px solid #E2E8F0;
    }
    .vs__search { font-size: 13px; font-family: 'Plus Jakarta Sans', sans-serif; }
    &.vs--open .vs__dropdown-toggle { border-color: #3B82F6; box-shadow: 0 0 0 3px rgba(#3B82F6, 0.1); }
  }

  .select-filter { min-width: 160px; }

  .filter-date {
    border: 1px solid #E2E8F0;
    border-radius: 8px;
    padding: 6px 10px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    color: #1E293B;
    width: 130px;

    &:focus { outline: none; border-color: #3B82F6; }
  }
}

// Dark mode
.dark-layout {
  .pedmat-page {
    .header-title { color: #E2E8F0; }

    .kpi-card { background: #1E293B; border-color: #334155; }
    .kpi-value { color: #F1F5F9; }

    .filters-bar { background: #1E293B; border-color: #334155; }

    .search-box__input { border-color: #334155; color: #E2E8F0; &::placeholder { color: #475569; } &:focus { border-color: #3B82F6; } }

    .pill { border-color: #475569; color: #94A3B8; &:hover { border-color: #3B82F6; color: #3B82F6; } &.active { background: #3B82F6; border-color: #3B82F6; color: white; } }

    .filter-date { background: #0F172A; border-color: #334155; color: #E2E8F0; }

    .list-card { background: #1E293B; border-color: #334155; }
    .list-card__footer { border-color: #334155; }

    .table-head th { color: #94A3B8; border-color: #334155; }
    .table-row td { border-color: #334155; }
    .table-row:hover td { background: rgba(255,255,255,0.03); }

    .cell-text { color: #E2E8F0; }
    .cell-mono { color: #E2E8F0; }
    .action-btn { color: #94A3B8; }

    .details-inner { background: #0F172A; border-color: #334155; }
    .details-head th { color: #94A3B8; border-color: #334155; }
    .details-row td { color: #E2E8F0; }

    .select-styled .vs__dropdown-toggle { border-color: #334155; background: #0F172A; }
    .select-styled .vs__search { color: #E2E8F0; }
  }
}
</style>
